"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { SupabaseClient } from "@supabase/supabase-js";
import { ChevronDown, LoaderCircle, RefreshCw, SlidersHorizontal, X } from "lucide-react";
import { useRunyiBackTrail } from "@/lib/use-runyi-back-trail";

export type AiModelChoice = {
  provider_key: string;
  provider_name: string;
  provider_status: string;
  model_key: string;
  display_name: string;
  capability_keys: string[];
  supported_modes: string[];
  execution_state: string;
  executable: boolean;
  availability: string;
};

type AiConfiguration = {
  application_entity_key: string;
  environment_key: string;
  workload_key: string;
  provider_key: string;
  model_key: string;
};

export function UniversalAiModelPicker({
  supabase, connected, mode, selectedProviderKey, selectedModelKey, onSelect,
  applicationEntityKey, projectId, environmentKey = "PRODUCTION", onConfigured, toolbarCompact = false,
}: {
  supabase: SupabaseClient | null;
  connected: boolean;
  mode?: "chat" | "work";
  selectedProviderKey?: string;
  selectedModelKey?: string;
  onSelect?: (providerKey: string, modelKey: string) => void;
  applicationEntityKey?: string;
  projectId?: string;
  environmentKey?: string;
  onConfigured?: () => void;
  toolbarCompact?: boolean;
}) {
  const [models, setModels] = useState<AiModelChoice[]>([]);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [draftProvider, setDraftProvider] = useState("");
  const [draftModel, setDraftModel] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [current, setCurrent] = useState<AiConfiguration | null>(null);
  const [expanded, setExpanded] = useState(false);
  useRunyiBackTrail(expanded, setExpanded);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const resourceScope = applicationEntityKey ? "application" : projectId ? "project" : "workspace";
  const workloadKey = mode ?? "default";
  const providerKey = onSelect ? selectedProviderKey ?? "" : draftProvider;
  const modelKey = onSelect ? selectedModelKey ?? "" : draftModel;

  const loadCatalogue = useCallback(async (offset = 0) => {
    if (!supabase || !connected) {
      setModels([]); setCurrent(null); setNextOffset(null); return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke<{
        error?: string;
        models?: AiModelChoice[];
        next_offset?: number | null;
        total_count?: number;
        application_config?: AiConfiguration[];
        project_config?: AiConfiguration[];
      }>("runyi-ai", { body: {
        action: "model_catalogue", offset,
        application_entity_key: applicationEntityKey || undefined,
        project_id: projectId || undefined,
      } });
      if (error || data?.error) throw new Error(data?.error ?? error?.message ?? "AI catalogue unavailable");
      const received = data?.models ?? [];
      setModels((before) => offset === 0
        ? received
        : [...before, ...received.filter((row) => !before.some((known) =>
            known.provider_key === row.provider_key && known.model_key === row.model_key))]);
      setNextOffset(data?.next_offset ?? null);
      setTotalCount(data?.total_count ?? received.length);
      if (applicationEntityKey || projectId) {
        const configured = (projectId ? data?.project_config : data?.application_config)?.find((row) =>
          row.workload_key === workloadKey && row.environment_key === environmentKey) ?? null;
        setCurrent(configured);
        if (!onSelectRef.current && configured && offset === 0) {
          setDraftProvider(configured.provider_key);
          setDraftModel(configured.model_key);
        }
      }
      setNotice("");
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Could not load AI infrastructure.");
    } finally {
      setLoading(false);
    }
  }, [applicationEntityKey, projectId, connected, environmentKey, workloadKey, supabase]);

  useEffect(() => { void loadCatalogue(); }, [loadCatalogue]);
  useEffect(() => {
    const onFocus = () => void loadCatalogue();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadCatalogue]);

  const providers = useMemo(() =>
    Array.from(new Map(models.map((row) => [row.provider_key, row.provider_name])).entries()),
    [models]);
  const available = models.filter((row) =>
    row.provider_key === providerKey && (!mode || row.supported_modes.includes(mode)));
  const selected = available.find((row) => row.model_key === modelKey);
  const change = (provider: string, model: string) => {
    if (onSelect) onSelect(provider, model);
    else { setDraftProvider(provider); setDraftModel(model); }
    setNotice("");
  };

  const configure = async () => {
    if (!supabase || (!applicationEntityKey && !projectId) || !selected?.executable) return;
    setSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke<{
        error?: string; application_config?: AiConfiguration; project_config?: AiConfiguration;
      }>("runyi-ai", { body: {
        action: projectId ? "configure_project_model" : "configure_application_model",
        application_entity_key: applicationEntityKey,
        project_id: projectId,
        environment_key: environmentKey,
        workload_key: workloadKey,
        provider_key: providerKey,
        model_key: modelKey,
      } });
      const saved = projectId ? data?.project_config : data?.application_config;
      if (error || data?.error || !saved)
        throw new Error(data?.error ?? error?.message ?? "Model configuration failed.");
      setCurrent(saved);
      setNotice("Saved to this " + resourceScope + " through OWNER ROOT.");
      onConfigured?.();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Configuration was not saved.");
    } finally {
      setSaving(false);
    }
  };

  const selectedName = selected?.display_name ?? (modelKey || "Automatic");
  const providerName = providers.find(([key]) => key === providerKey)?.[1] ?? (providerKey || "RUNYI default");
  const dialog = expanded && <div className="fixed inset-0 z-[95] flex items-end justify-center bg-black/65 p-2 sm:items-center" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setExpanded(false); }}>
        <section role="dialog" aria-modal="true" aria-label="AI provider and model settings" className="max-h-[80dvh] w-full max-w-lg overflow-y-auto overscroll-contain rounded-2xl border border-foreground/15 bg-background p-4 shadow-2xl">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="min-w-0"><p className="text-sm font-semibold">{resourceScope === "workspace" ? `RUNYI AI · ${mode === "work" ? "Work" : "Chat"}` : "AI provider & model"}</p>
              <p className="text-[10px] text-foreground/50">{models.length} of {totalCount} registered models loaded</p></div>
            <div className="flex items-center gap-2">
              <button type="button" aria-label="Refresh AI provider catalogue" className="grid size-9 place-items-center rounded-lg border border-foreground/10" onClick={() => void loadCatalogue()} disabled={!connected || loading}>{loading ? <LoaderCircle className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}</button>
              <button type="button" aria-label="Close AI configuration" className="grid size-9 place-items-center rounded-lg border border-foreground/10" onClick={() => setExpanded(false)}><X className="size-4" /></button>
            </div>
          </div>
          <div className="grid min-w-0 gap-3 sm:grid-cols-2">
            <label className="min-w-0 text-[11px] font-medium text-foreground/60">Provider
              <select aria-label="AI provider" className="mt-1 h-11 w-full min-w-0 rounded-xl border border-foreground/15 bg-background px-3 text-sm text-foreground" value={providerKey} onChange={(event) => change(event.target.value, "")} disabled={!connected || saving}>
                <option value="">{resourceScope === "workspace" ? "Existing RUNYI default" : "Select assigned provider"}</option>
                {providers.map(([key, name]) => <option key={key} value={key}>{name}</option>)}
              </select>
            </label>
            <label className="min-w-0 text-[11px] font-medium text-foreground/60">Model
              <select aria-label="AI model" className="mt-1 h-11 w-full min-w-0 rounded-xl border border-foreground/15 bg-background px-3 text-sm text-foreground" value={modelKey} onChange={(event) => change(providerKey, event.target.value)} disabled={!providerKey || !connected || saving}>
                <option value="">Select model</option>
                {available.map((row) => <option key={row.model_key} value={row.model_key} disabled={!row.executable}>{row.display_name} · {row.executable ? "Executable" : row.execution_state}</option>)}
              </select>
            </label>
          </div>
          {selected && <p className="mt-3 break-words text-[11px] text-foreground/55">{selected.provider_name} · {selected.availability} · {(selected.capability_keys ?? []).join(", ") || "Capabilities not declared"}</p>}
          {providerKey && !available.length && <p className="mt-3 text-[11px] text-foreground/55">No supported registered models for this selection.</p>}
          {resourceScope !== "workspace" && <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <span className="text-[11px] text-foreground/55">{current ? `Current: ${current.provider_key} / ${current.model_key}` : "No provider/model configured"}</span>
            <button type="button" className="min-h-10 rounded-xl border border-primary/30 bg-primary/10 px-4 text-xs font-semibold text-primary disabled:opacity-40" disabled={!selected?.executable || saving || !connected} onClick={() => void configure()}>{saving ? "Saving…" : "Save provider & model"}</button>
          </div>}
          {nextOffset != null && <button type="button" className="mt-3 min-h-10 w-full rounded-xl border border-foreground/10 text-xs" disabled={loading} onClick={() => void loadCatalogue(nextOffset)}>Load more models</button>}
          {notice && <p role="status" className="mt-3 break-words text-xs text-foreground/65">{notice}</p>}
          <p className="mt-3 text-[11px] leading-5 text-foreground/50">Register and authorize providers in New Infrastructure; assign verified resources from Marketplace. Catalogue presence alone does not authorize execution.</p>
        </section>
      </div>;
  return (
    <section className={toolbarCompact ? "shrink-0" : "min-w-0"} aria-label="AI provider and model configuration">
      <button type="button" aria-expanded={expanded} aria-label="Configure AI provider and model"
        title={toolbarCompact ? `${providerName} / ${selectedName}` : undefined}
        onClick={() => setExpanded(true)}
        className={toolbarCompact
          ? "grid size-8 shrink-0 place-items-center rounded-lg text-foreground/55 transition hover:bg-foreground/[0.06] hover:text-foreground"
          : "flex min-h-10 w-full min-w-0 items-center gap-2 rounded-xl border border-foreground/10 bg-foreground/[0.025] px-3 py-2 text-left hover:border-primary/30"}>
        <SlidersHorizontal className={toolbarCompact ? "size-4" : "size-4 shrink-0 text-primary"} />
        {!toolbarCompact && <>
          <span className="min-w-0 flex-1 truncate text-[11px] font-semibold"><span className="text-foreground/50">AI</span> · {providerName} / {selectedName}</span>
          <ChevronDown className="size-4 shrink-0 text-foreground/50" />
        </>}
      </button>
      {toolbarCompact ? (typeof document !== "undefined" && dialog ? createPortal(dialog, document.body) : null) : dialog}
    </section>
  );
}
